import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert'; // Import Alert for Snackbar styling
import { addVisit } from '../../services/visitService'; // Import the addVisit service

// Snackbar Alert Component
const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddVisitModal = ({ patientId, open, onClose, onVisitAdded }) => {
    const [formData, setFormData] = useState({
        diagnose: '',
        anamnesa: '',
        echo_report: '',
        treatment: '',
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData({
                diagnose: '',
                anamnesa: '',
                echo_report: '',
                treatment: '',
            });
        }
    }, [open]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async () => {
        if (saving) return;
        if (!formData.diagnose.trim()) {
            setSnackbar({ open: true, message: 'Please enter a diagnosis.', severity: 'error' });
            return;
        }
        setSaving(true);
        const visitData = {
            patient_id: patientId,
            diagnose: formData.diagnose || '',
            anamnesa: formData.anamnesa || '',
            echo_report: formData.echo_report || '',
            treatment: formData.treatment || ''
        };

        try {
            const response = await addVisit(visitData);
            if (response.success && response.data?.status === 'success') {
                const newVisit = {
                    id: response.data.id,
                    patient_id: patientId,
                    diagnose: visitData.diagnose,
                    anamnesa: visitData.anamnesa,
                    echo_report: visitData.echo_report,
                    treatment: visitData.treatment,
                    visit_date: new Date().toISOString() // will approximate since backend uses NOW(); could refetch if precision needed
                };
                onVisitAdded(newVisit);
                setSnackbar({ open: true, message: 'Visit added successfully!', severity: 'success' });
                onClose();
            } else {
                setSnackbar({ open: true, message: 'Error adding visit', severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'An error occurred. Please try again.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ open: false, message: '', severity: 'success' });
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Add New Visit</DialogTitle>
                <DialogContent>
                    <TextField
                        name="diagnose"
                        label="Diagnose"
                        value={formData.diagnose}
                        onChange={handleInputChange}
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        sx={{ '& textarea': { resize: 'vertical' } }} // Enable vertical resizing
                    />
                    <TextField
                        name="anamnesa"
                        label="Anamnesis"
                        value={formData.anamnesa}
                        onChange={handleInputChange}
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        sx={{ '& textarea': { resize: 'vertical' } }}
                    />
                    <TextField
                        name="echo_report"
                        label="ECHO Report"
                        value={formData.echo_report}
                        onChange={handleInputChange}
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        sx={{ '& textarea': { resize: 'vertical' } }}
                    />
                    <TextField
                        name="treatment"
                        label="Treatment"
                        value={formData.treatment}
                        onChange={handleInputChange}
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        sx={{ '& textarea': { resize: 'vertical' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving}>{saving ? 'Saving…' : 'Add Visit'}</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Notification */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%', backgroundColor: snackbar.severity === 'success' ? '#449998' : '#f44336' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddVisitModal;
